/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SectionTop from '../layout/SectionTop';
import Why2Bytes from '../layout/Why2Bytes';
import SectionBottom from '../layout/SectionBottom';
import Services from '../components/Services'

import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';

import { Box as div } from '@mui/material';

const CssCenter = css`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CssProcessContainer = css`
  display: flex;
  flex-direction: column;
  margin: auto;
  
  /* PC version - 1280px and up */
  @media (min-width: 1280px) {
    width: 100%;
    max-width: 1278px;
  }
  
  /* Tablet version - 992px */
  @media (min-width: 993px) and (max-width: 1279px) {
    width: 100%;
    max-width: 944px;
  }
  
  /* Mobile version */
  @media (max-width: 992px) {
    width: 100%;
    max-width: 360px;
  }
`;

const CssStepGrid = css`
  display: flex;
  align-items: center;
  
  /* PC version - 1280px and up */
  @media (min-width: 1280px) {
    gap: 0;
    flex-direction: row;
    justify-content: space-between;
  }
  
  /* Tablet version - 992px */
  @media (min-width: 993px) and (max-width: 1279px) {
    gap: 0;
    flex-direction: row;
    justify-content: space-between;
  }
  
  /* Mobile version */
  @media (max-width: 992px) {
    flex-wrap: wrap;
    gap: 16px;
    justify-content: space-between;
  }
`;

const CssDotSeparator = css`
  flex-shrink: 0;
  height: 5px;
  background-image: radial-gradient(circle, #d5d5d5 2px, transparent 2px);
  background-size: 12px 5px;
  background-repeat: repeat-x;
  background-position: center;
  
  /* PC version - 1280px and up */
  @media (min-width: 1280px) {
    width: 29px;
  }
  
  /* Tablet version - 992px */
  @media (min-width: 993px) and (max-width: 1279px) {
    width: 29px;
  }
  
  /* Mobile version - hide on mobile */
  @media (max-width: 992px) {
    display: none;
  }
`;

const CssStepItem = css`
  background-color: #f5f5fb;
  display: flex;
  flex-direction: column;
  
  /* PC version - 1280px and up */
  @media (min-width: 1280px) {
    width: 160px;
    height: 180px;
  }
  
  /* Tablet version - 992px */
  @media (min-width: 993px) and (max-width: 1279px) {
    width: 140px;
    height: 206px;
  }
  
  /* Mobile version */
  @media (max-width: 992px) {
    width: 152px;
    height: 132px;
  }
`;

const CssStepItemFullWidth = css`
  /* Last item spans full width on mobile */
  @media (max-width: 992px) {
    width: 320px;
    margin: 0 auto;
  }
`;

const CssStepTitle = css`
  background-color: #743fa3;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 700;
  text-align: center;
  letter-spacing: -1px;

  /* PC version - 1280px and up */
  @media (min-width: 1280px) {
    height: 56px;
    font-size: 20px;
    line-height: 30px;
  }

  /* Tablet version - 992px */
  @media (min-width: 993px) and (max-width: 1279px) {
    height: 56px;
    font-size: 20px;
    line-height: 30px;
  }

  /* Mobile version */
  @media (max-width: 992px) {
    height: 40px;
    font-size: 14px;
    line-height: 22px;
    letter-spacing: -0.5px;
  }
`;

const CssStepContent = css`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #444444;
  padding: 16px;
  letter-spacing: -1px;
  white-space: pre-line;

  /* PC version - 1280px and up */
  @media (min-width: 1280px) {
    flex: 1;
    font-size: 17px;
    line-height: 26px;
  }

  /* Tablet version - 992px */
  @media (min-width: 993px) and (max-width: 1279px) {
    flex: 1;
    font-size: 17px;
    line-height: 26px;
  }

  /* Mobile version */
  @media (max-width: 992px) {
    flex: 1;
    font-size: 14px;
    line-height: 22px;
    letter-spacing: -0.5px;
  }
`;

const CssProcessTitle = css`
  color: #743fa3;
  font-weight: 700;
  letter-spacing: -1px;
  margin-bottom: 90px;

  /* PC version - 1280px and up */
  @media (min-width: 1280px) {
    font-size: 36px;
    line-height: 50px;
  }

  /* Tablet version - 992px */
  @media (min-width: 993px) and (max-width: 1279px) {
    font-size: 36px;
    line-height: 50px;
  }

  /* Mobile version */
  @media (max-width: 992px) {
    font-size: 24px;
    line-height: 34px;
    letter-spacing: -0.5px;
    margin-bottom: 40px;
  }
`;

const CssServicesContainer = css`
  display: flex;
  flex-direction: column;
  margin: auto;
  
  /* PC version - 1280px and up */
  @media (min-width: 1280px) {
    width: 100%;
    max-width: 1278px;
  }
  
  /* Tablet version - 992px */
  @media (min-width: 993px) and (max-width: 1279px) {
    width: 100%;
    max-width: 944px;
  }
  
  /* Mobile version */
  @media (max-width: 992px) {
    width: 100%;
    max-width: 320px;
  }
`;

const CssServicesTitle = css`
  color: #743fa3;
  font-weight: 700;
  letter-spacing: -1px;

  /* PC version - 1280px and up */
  @media (min-width: 1280px) {
    font-size: 36px;
    line-height: 50px;
    margin-bottom: 40px;
  }

  /* Tablet version - 992px */
  @media (min-width: 993px) and (max-width: 1279px) {
    font-size: 36px;
    line-height: 50px;
    margin-bottom: 32px;
  }

  /* Mobile version */
  @media (max-width: 992px) {
    font-size: 24px;
    line-height: 33.6px;
    letter-spacing: -0.5px;
    margin-bottom: 24px;
  }
`;

const CssServicesGrid = css`
  display: grid;
  gap: 32px;
  
  /* PC version - 1280px and up: 2 columns */
  @media (min-width: 1280px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  /* Tablet version - 992px: 1 column */
  @media (min-width: 993px) and (max-width: 1279px) {
    grid-template-columns: 1fr;
  }
  
  /* Mobile version: 1 column */
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const CssServiceCard = css`
  background-color: #ffffff;
  display: flex;
  
  /* PC version - 1280px and up */
  @media (min-width: 1280px) {
    flex-direction: row;
    height: 240px;
  }
  
  /* Tablet version - 992px */
  @media (min-width: 993px) and (max-width: 1279px) {
    flex-direction: row;
    height: 200px;
  }
  
  /* Mobile version */
  @media (max-width: 992px) {
    flex-direction: column;
    height: auto;
  }
`;

const CssServiceImageWrapper = css`
  background-color: rgba(232, 232, 232, 0.2);
  flex-shrink: 0;
  
  /* PC version - 1280px and up */
  @media (min-width: 1280px) {
    width: 200px;
    height: 240px;
  }
  
  /* Tablet version - 992px */
  @media (min-width: 993px) and (max-width: 1279px) {
    width: 200px;
    height: 200px;
  }
  
  /* Mobile version */
  @media (max-width: 992px) {
    width: 100%;
    height: 123px;
  }
`;

const CssServiceImage = css`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
`;

const CssServiceContent = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 24px;
  
  /* PC version - 1280px and up */
  @media (min-width: 1280px) {
    padding: 38px 24px;
  }
  
  /* Tablet version - 992px */
  @media (min-width: 993px) and (max-width: 1279px) {
    padding: 38px 24px;
  }
  
  /* Mobile version */
  @media (max-width: 992px) {
    padding: 16px;
  }
`;

const CssServiceTitle = css`
  color: #444444;
  font-weight: 500;
  letter-spacing: -1px;
  margin-bottom: 14px;
  
  /* PC version - 1280px and up */
  @media (min-width: 1280px) {
    font-size: 32px;
    line-height: 38.4px;
  }
  
  /* Tablet version - 992px */
  @media (min-width: 993px) and (max-width: 1279px) {
    font-size: 32px;
    line-height: 38.4px;
  }
  
  /* Mobile version */
  @media (max-width: 992px) {
    font-size: 20px;
    line-height: 28px;
    letter-spacing: -0.5px;
  }
`;

const CssServiceDescription = css`
  color: rgba(68, 68, 68, 0.8);
  letter-spacing: -1px;
  
  /* PC version - 1280px and up */
  @media (min-width: 1280px) {
    font-size: 20px;
    line-height: 36px;
  }
  
  /* Tablet version - 992px */
  @media (min-width: 993px) and (max-width: 1279px) {
    font-size: 20px;
    line-height: 36px;
  }
  
  /* Mobile version */
  @media (max-width: 992px) {
    font-size: 14px;
    line-height: 22.4px;
    letter-spacing: -0.5px;
  }
`;

function SvgMarketingProcess() {
  const { t, i18n } = useTranslation();
  const pt = (v) => parse(i18n.t(v));

  const steps = [
    {
      title: t('ContentMarketing_25'),
      content: t('ContentMarketing_26')
    },
    {
      title: t('ContentMarketing_27'),
      content: t('ContentMarketing_28')
    },
    {
      title: t('ContentMarketing_29'),
      content: t('ContentMarketing_30')
    },
    {
      title: t('ContentMarketing_31'),
      content: t('ContentMarketing_32')
    },
    {
      title: t('ContentMarketing_33'),
      content: t('ContentMarketing_34')
    }
  ];

  return (
    <div css={CssProcessContainer}>
      <h3 css={CssProcessTitle}>{t('ContentMarketing_35')}</h3>
      <div css={CssStepGrid}>
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            <div
              css={[CssStepItem, index === 4 && CssStepItemFullWidth]}
            >
              <div css={CssStepTitle}>{step.title}</div>
              <div css={CssStepContent}>{step.content}</div>
            </div>
            {index < steps.length - 1 && <div css={CssDotSeparator} />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function Content() {
  const { t, i18n } = useTranslation();
  const pt = (v) => parse(i18n.t(v));

  useEffect(() => {
    $(window).trigger('scroll');
    $(window).scrollTop(0);
  }, []);
  return (
    <>
      <section className="section_discript">
        <div className="inner">
          <strong className="title">{pt('ContentMarketing_2')}</strong>

          <div className="step_box">
            <dl>
              <dt>
                <span className="num">1</span>
                {pt('ContentMarketing_3')}
              </dt>
              <dd>{pt('ContentMarketing_4')}</dd>
            </dl>
            <dl>
              <dt>
                <span className="num">2</span>
                {pt('ContentMarketing_5')}
              </dt>
              <dd>{pt('ContentMarketing_6')}</dd>
            </dl>
            <dl>
              <dt>
                <span className="num">3</span>
                {pt('ContentMarketing_7')}
              </dt>
              <dd>{pt('ContentMarketing_8')}</dd>
            </dl>
          </div>
        </div>
      </section>

      <section className="section_discript">
        <div className="inner">
          <SvgMarketingProcess />
        </div>
      </section>

      <section className="section_player">
        <div className="text_box">
          <div className="inner d_table">
            <div className="d_cell text_primary">
              {pt('ContentMarketing_11')}
            </div>
            <div className="d_cell btn_area">
              <a
                href={`/${i18n.language}/Contact`}
                className="btn_basic btn_primary btn_xl btn_pop_open"
              >
                {pt('ForStages_20')}
                {/* 문의/업무의뢰 */}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="section_discript">
        <div className="inner" css={CssServicesContainer}>
          <h3 css={CssServicesTitle}>{t('ContentMarketing_36')}</h3>
          <div css={CssServicesGrid}>
            {[
              {
                titleKey: 'ContentMarketing_37',
                descKey: 'ContentMarketing_38',
                useParser: true,
                image: '/static/img/content-marketing/1.png'
              },
              {
                titleKey: 'ContentMarketing_39',
                descKey: 'ContentMarketing_40',
                useParser: true,
                image: '/static/img/content-marketing/2.png'
              },
              {
                titleKey: 'ContentMarketing_41',
                descKey: 'ContentMarketing_42',
                useParser: false,
                image: '/static/img/content-marketing/3.png'
              },
              {
                titleKey: 'ContentMarketing_43',
                descKey: 'ContentMarketing_44',
                useParser: false,
                image: '/static/img/content-marketing/4.png'
              },
              {
                titleKey: 'ContentMarketing_45',
                descKey: 'ContentMarketing_46',
                useParser: true,
                image: '/static/img/content-marketing/5.png'
              },
              {
                titleKey: 'ContentMarketing_47',
                descKey: 'ContentMarketing_48',
                useParser: true,
                image: '/static/img/content-marketing/6.png'
              },
              {
                titleKey: 'ContentMarketing_49',
                descKey: 'ContentMarketing_50',
                useParser: true,
                image: '/static/img/content-marketing/7.png'
              },
              {
                titleKey: 'ContentMarketing_51',
                descKey: 'ContentMarketing_52',
                useParser: true,
                image: '/static/img/content-marketing/8.png'
              }
            ].map((service, index) => (
              <div key={index} css={CssServiceCard}>
                <div css={CssServiceImageWrapper}>
                  <div 
                    css={CssServiceImage}
                    style={{ backgroundImage: `url(${service.image})` }}
                  ></div>
                </div>
                <div css={CssServiceContent}>
                  <h4 css={CssServiceTitle}>{t(service.titleKey)}</h4>
                  <p css={CssServiceDescription}>
                    {service.useParser ? pt(service.descKey) : t(service.descKey)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function PlayerSupport(props) {
  return (
    <div id="container" className="cont_cm">
      <SectionTop title="Content Marketing" mtitle="CM" />
      <Content />
      <Services />
      <SectionBottom />
    </div>
  );
}

export default PlayerSupport;
